<div class="mb-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">

  <!-- Header Row 1: Back Button -->
  <div class="mb-4">
    <app-btn-back [routeTo]="'/groups/' + churchId"></app-btn-back>
  </div>

  <!-- Header Row 2: Title and Subtitle -->
  <div class="mb-6">
    <h1 class="text-2xl font-bold text-gray-900 capitalize">
      Governance & Affiliation
    </h1>
    <p class="text-xs text-blueGray-500 mt-1">
      Upward denominational oversight, jurisdiction lineage, and appointed leadership.
    </p>
  </div>

  @let oversight = (oversight$ | async);
  @let loading = (loading$ | async);
  @let isChurchAdmin = !!(myRoles$ | async)?.admin;
  @let invites = (invites$ | async) || [];
  @let joinRequests = (joinRequests$ | async) || [];

  <!-- Pending Organization Invitations (church admins only) -->
  @if (isChurchAdmin && invites.length > 0) {
  <div class="border border-amber-200 bg-amber-50/40 rounded-xl p-5 mb-6 flex flex-col gap-4">
    <div>
      <h3 class="text-sm font-bold text-blueGray-800 tracking-tight">
        Pending Invitations ({{ invites.length }})
      </h3>
      <p class="text-[11px] text-blueGray-500 mt-0.5">
        Organizations inviting this church to join their org chart. A church can belong to one organization at a time.
      </p>
    </div>

    @for (invite of invites; track invite.id) {
    <div class="bg-white rounded-xl border border-blueGray-100 shadow-sm p-4 flex flex-col gap-3">
      <div class="flex items-start gap-3">
        @if (invite.organizationLogoUrl) {
        <img [src]="invite.organizationLogoUrl" class="h-11 w-11 rounded-xl object-cover border border-blueGray-200 flex-shrink-0"
          [alt]="invite.organizationName" />
        } @else {
        <div class="h-11 w-11 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0">
          <i class="fas fa-sitemap"></i>
        </div>
        }
        <div class="min-w-0 flex-1">
          <h4 class="text-sm font-bold text-blueGray-800">{{ invite.organizationName }}</h4>
          <p class="text-[11px] text-blueGray-500 mt-0.5">
            Invited by {{ invite.invitedBy?.name || 'an organization admin' }} ·
            expires {{ invite.expiresAt.toDate() | date:'mediumDate' }}
          </p>
          <p class="text-[11px] text-blueGray-700 mt-2">
            <span class="font-semibold">Placement:</span>
            {{ (invite.targetLineage?.length ? invite.targetLineage : [invite.targetNodeName])?.join(' › ') }}
            › <span class="font-semibold">This church</span>
          </p>
          @if (invite.message) {
          <p class="text-[11px] text-blueGray-600 italic mt-2 bg-blueGray-50 rounded-lg p-2.5 border border-blueGray-100">
            "{{ invite.message }}"
          </p>
          }
          <p class="text-[10px] text-blueGray-400 mt-2">
            <i class="fas fa-shield-alt mr-1"></i>
            On joining, this church's admins get church-level access in the organization. Attendance and growth are
            shared; finances are never shared.
          </p>
        </div>
      </div>

      @if (rejectingInviteId === invite.id) {
      <div class="flex flex-col gap-2">
        <textarea [(ngModel)]="rejectReason" rows="2" maxlength="500" placeholder="Reason for declining (optional)"
          class="w-full px-3 py-2 text-xs rounded-lg border border-blueGray-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"></textarea>
        <div class="flex items-center justify-end gap-2">
          <button type="button" (click)="cancelReject()"
            class="px-3 py-1.5 text-xs font-semibold text-blueGray-600 hover:bg-blueGray-100 rounded-lg cursor-pointer">
            Back
          </button>
          <button type="button" (click)="respond(invite, false)" [disabled]="!!respondingInviteId"
            class="px-3 py-1.5 text-xs font-semibold text-white bg-rose-500 hover:bg-rose-600 disabled:opacity-50 rounded-lg cursor-pointer">
            Decline Invitation
          </button>
        </div>
      </div>
      } @else {
      <div class="flex items-center justify-end gap-2 pt-2 border-t border-blueGray-100">
        <button type="button" (click)="startReject(invite)" [disabled]="!!respondingInviteId"
          class="px-3 py-1.5 text-xs font-semibold text-blueGray-600 hover:bg-blueGray-100 disabled:opacity-50 rounded-lg cursor-pointer">
          Decline
        </button>
        <button type="button" (click)="confirmAcceptInvite(invite)" [disabled]="!!respondingInviteId"
          class="px-3 py-1.5 text-xs font-semibold text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50 rounded-lg shadow-2xs flex items-center gap-1.5 cursor-pointer">
          @if (respondingInviteId === invite.id) {
          <i class="fas fa-spinner fa-spin text-[10px]"></i>
          }
          <span>Accept & Join</span>
        </button>
      </div>
      }
    </div>
    }
  </div>
  }

  @if (loading) {
  <div class="flex items-center justify-center p-12">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
  </div>
  } @else if (oversight?.organization) {

    <!-- Card 1: Denominational / Network Entity Overview -->
    <div class="bg-white rounded-xl border border-blueGray-100 shadow-sm p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div class="flex items-center gap-4">
        <div class="h-14 w-14 rounded-2xl bg-gradient-to-tr from-primary-50 to-primary-100 text-primary-600 font-extrabold flex items-center justify-center text-2xl shadow-2xs flex-shrink-0">
          <i class="fas fa-sitemap"></i>
        </div>
        <div>
          <div class="flex items-center gap-2">
            <h2 class="text-lg font-bold text-blueGray-800">
              {{ oversight?.organization?.name }}
            </h2>
            <span class="px-2 py-0.5 rounded text-[8px] font-bold uppercase bg-emerald-50 text-emerald-600 border border-emerald-100">
              {{ oversight?.organization?.status || 'Active' }}
            </span>
          </div>
          <p class="text-xs text-blueGray-500 mt-0.5">
            {{ oversight?.organization?.description || 'Ecclesiastical oversight and multi-level governing body.' }}
          </p>
        </div>
      </div>

      <div class="flex items-center gap-2 self-start sm:self-auto">
        <span class="text-xs bg-blueGray-50 border border-blueGray-200 px-3 py-1.5 rounded-lg text-blueGray-600 font-medium">
          {{ oversight?.lineage?.length || 0 }} Lineage Levels
        </span>
      </div>
    </div>

    <!-- Card 2: Chain of Oversight & Jurisdiction Lineage -->
    <div class="border border-blueGray-200/60 rounded-xl p-5 mb-6 flex flex-col gap-4">
      <div>
        <h3 class="text-sm font-bold text-blueGray-800 tracking-tight">
          Upward Governance Lineage
        </h3>
        <p class="text-[11px] text-blueGray-400 mt-0.5">
          Hierarchical path from Apex Governing Board down to this local assembly.
        </p>
      </div>

      <!-- Lineage Flow -->
      <div class="flex flex-col md:flex-row items-stretch md:items-center gap-3 w-full overflow-x-auto py-2">
        @for (node of oversight?.lineage; track node.id; let last = $last; let index = $index) {
        
        <!-- Node Card -->
        <div class="bg-white rounded-xl border border-blueGray-100 shadow-sm p-4 flex-1 min-w-[200px] flex flex-col justify-between">
          <div>
            <div class="flex items-center justify-between mb-1">
              <span class="px-2 py-0.5 rounded text-[8px] font-bold uppercase bg-primary-50 text-primary-600 border border-primary-100">
                {{ node.levelName }}
              </span>
              <span class="text-[10px] text-blueGray-400 font-mono">
                Level {{ node.depth }}
              </span>
            </div>
            <h4 class="font-bold text-xs text-blueGray-800 truncate mt-1">
              {{ node.name }}
            </h4>
          </div>

          <div class="mt-3 pt-2 border-t border-blueGray-100 flex items-center justify-between text-[10px] text-blueGray-500">
            <span>Jurisdiction</span>
            <span class="font-semibold text-emerald-600 uppercase text-[9px]">{{ node.status }}</span>
          </div>
        </div>

        <!-- Connector Arrow -->
        <div class="hidden md:flex items-center justify-center text-blueGray-300 px-1">
          <i class="fas fa-chevron-right text-xs"></i>
        </div>
        <div class="flex md:hidden items-center justify-center text-blueGray-300 py-1">
          <i class="fas fa-chevron-down text-xs"></i>
        </div>

        }

        <!-- Final Step: Current Local Assembly -->
        <div class="bg-primary-500 rounded-xl border border-primary-600 shadow-sm p-4 flex-1 min-w-[200px] text-white flex flex-col justify-between">
          <div>
            <div class="flex items-center justify-between mb-1">
              <span class="px-2 py-0.5 rounded text-[8px] font-bold uppercase bg-white/20 text-white border border-white/30">
                Local Church
              </span>
              <span class="text-[10px] text-primary-200">
                Assembly
              </span>
            </div>
            <h4 class="font-bold text-xs text-white truncate mt-1">
              This Church
            </h4>
          </div>

          <div class="mt-3 pt-2 border-t border-primary-400/50 flex items-center justify-between text-[10px] text-primary-100">
            <span>Member Assembly</span>
            <span class="font-bold text-white uppercase text-[9px]">Affiliated</span>
          </div>
        </div>

      </div>
    </div>

    <!-- Card 3: Appointed Lineage Leadership -->
    <div class="border border-blueGray-200/60 rounded-xl p-5 mb-6 flex flex-col gap-4">
      <div>
        <h3 class="text-sm font-bold text-blueGray-800 tracking-tight">
          Appointed Overseers & Leadership Roster
        </h3>
        <p class="text-[11px] text-blueGray-400 mt-0.5">
          Designated regional overseers, superintendents, and governing officials assigned to this lineage.
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        @for (leader of oversight?.leaders; track leader.id) {
        <div class="bg-white rounded-xl border border-blueGray-100 shadow-sm p-4 flex items-start gap-3.5">
          <!-- Avatar -->
          @if (leader.userPhotoUrl) {
          <img [src]="leader.userPhotoUrl" class="h-11 w-11 rounded-full object-cover border border-blueGray-200 flex-shrink-0" />
          } @else {
          <div class="h-11 w-11 rounded-full bg-primary-100 text-primary-600 font-bold text-xs flex items-center justify-center flex-shrink-0">
            {{ leader.userName | slice:0:2 | uppercase }}
          </div>
          }

          <div class="min-w-0 flex-1">
            <h4 class="text-xs font-bold text-blueGray-800 truncate">
              {{ leader.userName }}
            </h4>
            <p class="text-[11px] font-semibold text-primary-600 truncate">
              {{ leader.roleName }}
            </p>
            @if (leader.nodeName) {
            <p class="text-[10px] text-blueGray-500 truncate mt-0.5">
              <i class="fas fa-sitemap text-[9px] mr-1"></i> {{ leader.nodeName }}
            </p>
            }
            <p class="text-[10px] text-blueGray-400 truncate">
              {{ leader.userEmail }}
            </p>
          </div>
        </div>
        } @empty {
        <div class="col-span-full py-6 text-center text-xs text-blueGray-400 italic">
          No external overseers or management leaders assigned directly to this lineage.
        </div>
        }
      </div>
    </div>

    <!-- Card 4: Leave Organization (church admins only) -->
    @if (isChurchAdmin && oversight?.organization; as organization) {
    <div class="border border-rose-200 bg-rose-50/30 rounded-xl p-5 mb-6 flex flex-col gap-3">
      <div>
        <h3 class="text-sm font-bold text-rose-700 tracking-tight">Leave Organization</h3>
        <p class="text-[11px] text-blueGray-500 mt-0.5">
          Removes this church from {{ organization.name }}'s org chart and revokes church-level roles in the
          organization. The organization's admins will be notified.
        </p>
      </div>
      <textarea [(ngModel)]="leaveReason" rows="2" maxlength="500" placeholder="Reason for leaving (optional)"
        class="w-full px-3 py-2 text-xs rounded-lg border border-blueGray-300 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400 resize-none bg-white"></textarea>
      <div class="flex justify-end">
        <button type="button" (click)="confirmLeave(organization)" [disabled]="isLeaving"
          class="px-4 py-2 text-xs font-semibold text-white bg-rose-500 hover:bg-rose-600 disabled:opacity-50 rounded-lg shadow-2xs flex items-center gap-1.5 cursor-pointer">
          @if (isLeaving) {
          <i class="fas fa-spinner fa-spin text-[10px]"></i>
          } @else {
          <i class="fas fa-sign-out-alt text-[10px]"></i>
          }
          <span>Leave {{ organization.name }}</span>
        </button>
      </div>
    </div>
    }

  } @else {

    <!-- Independent / Unaffiliated State -->
    <div class="bg-white rounded-2xl border border-dashed border-blueGray-200 p-12 text-center flex flex-col items-center justify-center">
      <div class="h-14 w-14 rounded-full bg-blueGray-50 text-blueGray-400 flex items-center justify-center text-xl mb-3">
        <i class="fas fa-sitemap"></i>
      </div>
      <h3 class="text-base font-bold text-blueGray-700 mb-1">
        Independent Local Assembly
      </h3>
      <p class="text-xs text-blueGray-500 max-w-md">
        This church is currently operating as a self-governing local assembly and is not affiliated with any regional or denominational oversight hierarchy.
      </p>
    </div>

    <!-- Join Requests Sent (church admins only) -->
    @if (isChurchAdmin && joinRequests.length > 0) {
    <div class="border border-blueGray-200/60 rounded-xl p-5 mt-6 flex flex-col gap-3">
      <h3 class="text-sm font-bold text-blueGray-800 tracking-tight">Requests Awaiting Review</h3>
      @for (request of joinRequests; track request.id) {
      <div class="bg-white rounded-xl border border-blueGray-100 shadow-sm p-4 flex items-center justify-between gap-3">
        <div class="min-w-0">
          <h4 class="text-xs font-bold text-blueGray-800 truncate">{{ request.organizationName }}</h4>
          <p class="text-[10px] text-blueGray-500">
            Sent by {{ request.requestedBy?.name || 'a church admin' }} ·
            expires {{ request.expiresAt.toDate() | date:'mediumDate' }}
          </p>
        </div>
        <button type="button" (click)="withdrawRequest(request)"
          class="px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 rounded-lg flex-shrink-0 cursor-pointer">
          Withdraw
        </button>
      </div>
      }
    </div>
    }

    <!-- Request to Join an Organization (church admins only) -->
    @if (isChurchAdmin) {
    <div class="border border-blueGray-200/60 rounded-xl p-5 mt-6 flex flex-col gap-3">
      <div>
        <h3 class="text-sm font-bold text-blueGray-800 tracking-tight">Request to Join an Organization</h3>
        <p class="text-[11px] text-blueGray-400 mt-0.5">
          Find your denomination or network. Its admins choose where the church sits in their org chart and approve
          the request. Attendance and growth are shared once you join; finances are never shared.
        </p>
      </div>
      <div class="relative">
        <input type="text" [ngModel]="orgSearchTerm" (ngModelChange)="onOrgSearchChange($event)"
          placeholder="Type an organization name to search"
          class="w-full px-3.5 py-2.5 text-xs rounded-xl border border-blueGray-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-blueGray-800 placeholder-blueGray-400" />
        @if (isSearchingOrgs) {
        <i class="fas fa-spinner fa-spin text-xs text-blueGray-400 absolute right-3 top-1/2 -translate-y-1/2"></i>
        }
      </div>

      @if (orgSearchTerm.trim().length >= 2 && !isSearchingOrgs) {
      @for (organization of orgSearchResults; track organization.id) {
      <div class="bg-white rounded-xl border border-blueGray-100 shadow-sm p-3 flex items-center justify-between gap-3">
        <div class="flex items-center gap-2.5 min-w-0">
          @if (organization.logoUrl) {
          <img [src]="organization.logoUrl" class="h-8 w-8 rounded-lg object-cover border border-blueGray-200 flex-shrink-0"
            [alt]="organization.name" />
          } @else {
          <div class="h-8 w-8 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0">
            <i class="fas fa-sitemap text-xs"></i>
          </div>
          }
          <div class="min-w-0">
            <h4 class="text-xs font-bold text-blueGray-800 truncate">{{ organization.name }}</h4>
            @if (organization.description) {
            <p class="text-[10px] text-blueGray-500 truncate">{{ organization.description }}</p>
            }
          </div>
        </div>
        @if (hasPendingRequestTo(organization.id, joinRequests, invites)) {
        <span class="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg flex-shrink-0">
          Pending
        </span>
        } @else {
        <button type="button" (click)="requestToJoin(organization)" [disabled]="!!requestingOrgId"
          class="px-3 py-1.5 text-xs font-semibold text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50 rounded-lg shadow-2xs flex items-center gap-1.5 flex-shrink-0 cursor-pointer">
          @if (requestingOrgId === organization.id) {
          <i class="fas fa-spinner fa-spin text-[10px]"></i>
          }
          <span>Request to join</span>
        </button>
        }
      </div>
      } @empty {
      <p class="text-[11px] text-blueGray-400">No organizations found starting with "{{ orgSearchTerm.trim() }}".</p>
      }
      @if (orgSearchResults.length > 0) {
      <textarea [(ngModel)]="joinMessage" rows="2" maxlength="500"
        placeholder="A short note for the organization's admins (optional)"
        class="w-full px-3 py-2 text-xs rounded-lg border border-blueGray-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"></textarea>
      }
      }
    </div>
    }

  }

</div>
